import { useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Fade,
} from "react-bootstrap";
import { ArrowRight } from "react-bootstrap-icons";
import { Link } from "react-router-dom";

import articlesData from "../articles-html/articlesData";
import { FAQ_CATEGORIES } from "../../libs/faqs";
import { routes } from "../../utils";
import FadeWrapper from "../../components/FadeWrapper";
import formatDate from "../../components/user-resources/utils/formatDate";
import useResourcesFiltersAndSort from "../../components/user-resources/useResourcesFiltersAndSort";
import ResourcesSortDropdown from "../../components/user-resources/ResourcesSortDropdown";
import ResourcesSearchInput from "../../components/user-resources/ResourcesSearchInput";

interface GuideEntry {
  slug: string;
  title: string;
  one_line_summary: string;
  categories: FAQ_CATEGORIES[];
  date: string;
}

export default function GuideOverview() {
  const guides = useMemo<GuideEntry[]>(
    () => Object.entries(articlesData).map(([slug, a]) => ({ slug, ...a })),
    []
  );

  const categories = useMemo(() => {
    const present = new Set<string>();
    const counts = new Map<string, number>();

    guides.forEach((g) =>
      g.categories.forEach((c) => {
        present.add(c);
        counts.set(c, (counts.get(c) || 0) + 1);
      })
    );

    const sortedCategories = Array.from(present)
      .sort()
      .map((cat) => `${cat} (${counts.get(cat)})`);

    return [`All`, ...sortedCategories];
  }, [guides]);

  const {
    query,
    setQuery,
    activeCategory,
    setActiveCategory,
    sortOption,
    setSortOption,
    result: sorted,
  } = useResourcesFiltersAndSort<GuideEntry>({
    items: guides,
    getTitle: (g) => g.title,
    getSearchText: (g) => g.one_line_summary,
    getDate: (g) => g.date,
    matchesCategory: (g, cat) =>
      cat === "All" ? true : g.categories.includes(cat as FAQ_CATEGORIES),
    initialSort: "A–Z",
  });

  return (
    <FadeWrapper>
      <div className="py-5 text-center text-white" style={{ background: "#1d446b" }}>
        <h1 className="fw-bold mb-2 display-5">Guides</h1>
        <p className="opacity-75 fs-5">
          Step-by-step guides for getting the most out of Photo51.
        </p>
        <ResourcesSearchInput query={query} setQuery={setQuery} />
      </div>

      <Container fluid className="pt-3 pb-5 px-md-5" style={{ minHeight: "1000px" }}>
        <Row className="gy-4 gx-5">
          {/* Sidebar */}
          <Col md={3} className="border-end pe-md-4" style={{ minWidth: 250 }}>
            <div className="sticky-top sticky-top-offset">
              <h6 className="fw-semibold mb-3">Filter by Category</h6>
              <div className="d-flex flex-wrap gap-2 mb-4">
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    size="sm"
                    variant={cat === activeCategory ? "primary" : "outline-primary"}
                    onClick={() => setActiveCategory(cat.replace(/ \([0-9]+\)$/, ''))}
                    className="rounded-pill px-2"
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
          </Col>

          <Col md={9}>
            <div className="d-flex justify-content-between align-items-center mb-0 ps-4">
              <span className="small text-muted">
                Showing {sorted.length} guide{sorted.length !== 1 && "s"}
              </span>
              <ResourcesSortDropdown setSortOption={setSortOption} sortOption={sortOption} />
            </div>

            {sorted.length === 0 ? (
              <p className="text-center text-muted py-5 fs-5">
                No guides found matching your search or filters.
              </p>
            ) : (
              <Row xs={1} sm={2} lg={3} className="g-4">
                {sorted.map((guide, idx) => (
                  <Fade in appear timeout={200 + idx * 100} key={guide.slug}>
                    <Col>
                      <Card
                        as={Link}
                        to={routes.guide(guide.slug)}
                        className="h-100 shadow-sm border-0 rounded-3 text-decoration-none guide-card"
                      >
                        <Card.Body className="p-4 d-flex flex-column">
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {guide.categories.map((cat) => (
                              <span
                                key={cat}
                                className="badge bg-light text-primary border"
                                style={{ fontSize: "0.7rem", fontWeight: 500 }}
                              >
                                {cat}
                              </span>
                            ))}
                          </div>
                          <Card.Title className="fw-bold mb-1 fs-6 text-dark">
                            {guide.title}
                          </Card.Title>
                          <div className="text-muted mb-2" style={{ fontSize: "0.8rem" }}>
                            {formatDate(guide.date)}
                          </div>
                          <Card.Text
                            className="text-muted mb-3"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {guide.one_line_summary}
                          </Card.Text>
                          <span className="mt-auto text-primary fw-semibold" style={{ fontSize: "0.85rem" }}>
                            Read Guide <ArrowRight className="ms-1" />
                          </span>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Fade>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </Container>

      <style>{`
        .guide-card { transition: all 0.3s ease; }
        .guide-card:hover { transform: translateY(-6px); box-shadow: 0 0.75rem 1.5rem rgba(0,0,0,0.1) !important; }
      `}</style>
    </FadeWrapper>
  );
}
